import React, { useEffect } from "react";
import { FormField } from "./FormField";
import styles from "./styles.module.scss";
import { useFormStore } from "src/store/dataEntryForm";
import { Select } from "antd";

interface SearchQueryGeographyFormProps {
  formId: string;
  onSubmit?: () => void;
}

const regionOptions = [
  { value: "northeast", label: "Northeast US" },
  { value: "southeast", label: "Southeast US" },
  { value: "midwest", label: "Midwest US" },
  { value: "southwest", label: "Southwest US" },
  { value: "west", label: "West US" },
];

const competitorPresenceOptions = [
  { value: "high", label: "High Competition" },
  { value: "medium", label: "Medium Competition" },
  { value: "low", label: "Low Competition" },
  { value: "none", label: "No Competition" },
];

const localizedKeywordOptions = [
  { value: "phone_case_nyc", label: "Phone Case - New York City" },
  { value: "phone_case_la", label: "Phone Case - Los Angeles" },
  { value: "phone_case_chi", label: "Phone Case - Chicago" },
  { value: "phone_case_hou", label: "Phone Case - Houston" },
  { value: "phone_case_mia", label: "Phone Case - Miami" },
];

export const SearchQueryGeographyForm: React.FC<
  SearchQueryGeographyFormProps
> = ({ formId, onSubmit }) => {
  const { initializeForm, resetForm, updateFormField, formData } =
    useFormStore();

  const handleRegionChange = (value: string) => {
    updateFormField(formId, "region", value);
  };

  const handleCompetitorPresenceChange = (value: string) => {
    updateFormField(formId, "competitorPresence", value);
  };

  const handleLocalizedKeywordChange = (value: string[]) => {
    updateFormField(formId, "localizedKeyword", value);
  };

  useEffect(() => {
    initializeForm(formId, {
      region: "",
      regionalCTR: "",
      competitorPresence: "",
      localizedKeyword: [],
    });

    return () => resetForm(formId);
  }, [formId, initializeForm, resetForm]);

  const regionValue = formData[formId]?.region || "";
  const competitorPresenceValue = formData[formId]?.competitorPresence || "";
  const localizedKeywordValue = formData[formId]?.localizedKeyword || [];

  return (
    <div className={styles.addSkuContainer}>
      {/* Region-Based Search Volume */}
      <div className={styles.row}>
        <div className={styles.fullWidthSelect}>
          <label className={styles.fieldLabel}>
            Region-Based Search Volume
          </label>
          <Select
            placeholder="Select region"
            value={regionValue || undefined}
            onChange={handleRegionChange}
            options={regionOptions}
            className={styles.fullWidthSelect}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            dropdownClassName="custom-select-dropdown"
          />
        </div>
      </div>

      {/* Regional CTR and Competitor Presence */}
      <div className={styles.row}>
        <FormField
          formId={formId}
          fieldName="regionalCTR"
          label="Regional Click-Through Rate (CTR), %"
          placeholder=""
          type="number"
          size="large"
        />
        <div className={styles.selectWrapper}>
          <label className={styles.fieldLabel}>
            Competitor Presence by Region
          </label>
          <Select
            placeholder="Select presence"
            value={competitorPresenceValue || undefined}
            onChange={handleCompetitorPresenceChange}
            options={competitorPresenceOptions}
            className={styles.typeSelect}
            style={{ width: "100%" }}
            dropdownClassName="custom-select-dropdown"
          />
        </div>
      </div>

      {/* Localized Keyword Performance */}
      <div className={styles.row}>
        <div className={styles.fullWidthSelect}>
          <label className={styles.fieldLabel}>
            Localized Keyword Performance
          </label>
          <Select
            mode="multiple"
            placeholder="Select keywords"
            value={localizedKeywordValue}
            onChange={handleLocalizedKeywordChange}
            options={localizedKeywordOptions}
            className={styles.fullWidthSelect}
            maxTagCount="responsive"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            dropdownClassName="custom-select-dropdown"
          />
        </div>
      </div>
    </div>
  );
};
